import * as React from "react";
import { ApolloError, gql, useMutation } from "@apollo/client";
import {
  FinishSignupMutationMutation,
  FinishSignupMutationMutationVariables
} from "../../../schemaTypes";
import { normalizeErrors } from "../../../utils/normalizeErrors";
import { NormalizedErrorMap } from "../../../types/NormalizedErrorMap";

const mapFinishSignupApolloError = (message: string): NormalizedErrorMap => {
  const normalizedMessage = message.toLowerCase();

  if (normalizedMessage.includes("firstname")) {
    return { firstname: message };
  }

  if (normalizedMessage.includes("lastname")) {
    return { lastname: message };
  }

  if (normalizedMessage.includes("username")) {
    return { username: message };
  }

  if (normalizedMessage.includes("location")) {
    return { locationId: message };
  }

  if (normalizedMessage.includes("pin")) {
    return { pin: message };
  }

  if (normalizedMessage.includes("referral")) {
    return { referralCode: message };
  }

  return { general: message };
};

const finishSignupMutation = gql`
  mutation FinishSignupMutation(
    $firstname: String!
    $lastname: String!
    $username: String!
    $locationId: String!
    $pin: String!
    $referralCode: String
  ) {
    finishSignup(
      firstname: $firstname
      lastname: $lastname
      username: $username
      locationId: $locationId
      pin: $pin
      referralCode: $referralCode
    ){
      errors{
        path
        message
      }
      message
    }
  }
`;

interface Props {
  onMessage?: (message: string) => void;
  children: (
    data: {
      submit: (
        values: FinishSignupMutationMutationVariables
      ) => Promise<{ errors?: NormalizedErrorMap | null; message?: string | null } | null>;
    }
  ) => React.ReactElement | null;
}

export const FinishSignup: React.FC<Props> = ({ children, onMessage }) => {
  const [_finishSignupMutation] = useMutation<
    FinishSignupMutationMutation,
    FinishSignupMutationMutationVariables
  >(finishSignupMutation);

  const submit = async (values: FinishSignupMutationMutationVariables) => {
    try {
      const { data } = await _finishSignupMutation({ variables: values });
      const errors = data?.finishSignup?.errors ?? null;
      const message = ("message" in (data?.finishSignup ?? {})) ? (data?.finishSignup as any).message : null;

      if (errors) {
        return { errors: normalizeErrors(errors), message };
      }

      if (!data?.finishSignup) {
        return {
          errors: { general: "An unknown error occurred" },
          message: null,
        };
      }

      if (message && onMessage) {
        onMessage(message);
      }

      return { errors: null, message };
    } catch (mutationError) {
      const apolloError = mutationError as ApolloError;
      const message =
        apolloError.graphQLErrors?.[0]?.message ||
        apolloError.message ||
        "An error occurred";

      return {
        errors: mapFinishSignupApolloError(message),
        message: null,
      };
    }
  };

  return children({ submit });
};